/**
 * Breadcrumbs Navigation Component
 *
 * Displays navigation breadcrumbs based on current route.
 * Automatically generates breadcrumb trail from URL path.
 */

import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useIsMobile';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';

// Route label configuration
const routeLabels: Record<string, string> = {
  '': 'dashboard',
  calendar: 'calendar',
  workorders: 'workOrders',
  'work-order-window': 'workOrderWindow',
  quotes: 'quotes',
  parties: 'parties',
  persons: 'persons',
  companies: 'companies',
  vehicles: 'vehicles',
  inspections: 'inspections',
  approvals: 'approvals',
  inventory: 'inventory',
  messages: 'messages',
  reports: 'reports',
  hr: 'hr',
  settings: 'settings',
};

// Intermediate segments that have no route of their own. Their crumb links to
// the page that lists them instead of building a path that 404s.
const crumbTargets: Record<string, string> = {
  '/parties/persons': '/parties',
  '/parties/companies': '/parties?tab=companies',
};

// Check if a segment is a dynamic ID (UUID or numeric)
function isDynamicSegment(segment: string): boolean {
  // UUID pattern
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  // Numeric ID
  const numericPattern = /^\d+$/;
  // Short ID (like mock data IDs)
  const shortIdPattern = /^[a-z]+-\d+$/i;

  return uuidPattern.test(segment) || numericPattern.test(segment) || shortIdPattern.test(segment);
}

export function Breadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('navigation');
  const isMobile = useIsMobile();

  // Workaround: SPA-navigointi ei toimi työmääräimen detail-sivulta (issue #842).
  // Pakota kova selainnavigointi kuten muuallakin. Poistetaan kun juurisyy on korjattu.
  const handleCrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, target: string) => {
    event.preventDefault();
    navigateAwayFromWorkOrderDetail(location.pathname, target, navigate);
  };

  // Parse path segments
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Don't show breadcrumbs on root/dashboard or on mobile (saves vertical space)
  if (pathSegments.length === 0 || isMobile) {
    return null;
  }

  // Build breadcrumb items
  const breadcrumbs: { label: string; path: string; isLast: boolean }[] = [];

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;

    // Get label for this segment
    let label: string;
    if (isDynamicSegment(segment)) {
      // For dynamic segments, use "Detail" or similar
      label = t('detail');
    } else {
      // Use translation key or fallback to capitalized segment
      const translationKey = routeLabels[segment];
      label = translationKey ? t(translationKey) : segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    breadcrumbs.push({
      label,
      path: crumbTargets[currentPath] ?? currentPath,
      isLast,
    });
  });

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label={t('breadcrumb')}
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: 0.5,
          },
        }}
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/"
          onClick={(event) => handleCrumbClick(event, '/')}
          color="inherit"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <HomeIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {t('home')}
          </Typography>
        </Link>

        {/* Path segments */}
        {breadcrumbs.map((crumb) =>
          crumb.isLast ? (
            <Typography
              key={crumb.path}
              variant="body2"
              color="text.primary"
              fontWeight={500}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.path}
              component={RouterLink}
              to={crumb.path}
              onClick={(event) => handleCrumbClick(event, crumb.path)}
              color="inherit"
              sx={{
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {crumb.label}
              </Typography>
            </Link>
          )
        )}
      </MuiBreadcrumbs>
    </Box>
  );
}
